import { m } from "#/paraglide/messages";
import {
  Badge,
  Button,
  createDataTableColumnHelper,
  DataTable,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  toast,
} from "@beweegbaas/ui";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { z } from "zod/v4";
import { DataTableActions } from "#/components/data-table-actions";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import {
  deleteAdminDeadLetters,
  getAdminDeadLetters,
  replayAdminDeadLetters,
} from "#/modules/admin.queue/dead-letters.functions";

const queueSearchSchema = z.object({
  page: z.number().int().min(1).default(1).catch(1),
});

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/admin/queue/")({
  validateSearch: queueSearchSchema,
  loaderDeps: ({ search }) => ({ page: search.page }),
  loader: ({ deps }) => getAdminDeadLetters({ data: deps }),
  component: QueueAdminOverview,
  errorComponent: QueueError,
});

function QueueError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

/** "Beweegbaas.Backend.Application.Handlers.Email.SendEmailCommand" → "SendEmailCommand". */
function shortTypeName(fullName: string): string {
  return fullName.split(".").pop() ?? fullName;
}

function formatTimestamp(timestamp: string): string {
  return new Date(timestamp).toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

type DeadLetter = ReturnType<typeof Route.useLoaderData>["deadLetters"][number];

function DeadLetterActions({ deadLetter }: { deadLetter: DeadLetter }) {
  const router = useRouter();
  const replay = useServerFn(replayAdminDeadLetters);
  const remove = useServerFn(deleteAdminDeadLetters);
  const [contentDialogOpen, setContentDialogOpen] = useState(false);

  async function handleReplay() {
    try {
      await replay({ data: { ids: [deadLetter.id] } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    }
    toast.success(m["admin.queue.hersteld_toast"]());
    await router.invalidate();
  }

  return (
    <>
      <DataTableActions
        actions={[
          {
            key: "content",
            label: "Bekijk inhoud",
            icon: <IconMaterialSymbolsVisibilityOutline className="size-4" />,
            onClick: () => setContentDialogOpen(true),
          },
          {
            key: "replay",
            label: "Replay",
            icon: <IconMaterialSymbolsRefresh className="size-4" />,
            disabled: deadLetter.replayable,
            onClick: () => void handleReplay(),
          },
          {
            key: "delete",
            label: "Verwijder",
            icon: <IconMaterialSymbolsDelete className="size-4" />,
            destructive: true,
            confirm: {
              title: m["admin.queue.verwijder_titel"](),
              message: m["admin.queue.verwijder_bericht"](),
              confirmLabel: "Verwijder",
            },
            onClick: async () => {
              await remove({ data: { ids: [deadLetter.id] } });
              toast.success("Bericht verwijderd.");
              await router.invalidate();
            },
          },
        ]}
      />
      <Dialog open={contentDialogOpen} onOpenChange={setContentDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{shortTypeName(deadLetter.messageType)}</DialogTitle>
          </DialogHeader>
          <pre className="max-h-96 overflow-auto rounded bg-black/5 p-3 text-xs">
            {prettyJson(deadLetter.messageJson)}
          </pre>
        </DialogContent>
      </Dialog>
    </>
  );
}

const helper = createDataTableColumnHelper<DeadLetter>();

const columns = helper.columns([
  helper.accessor("messageType", {
    header: "Bericht",
    cell: ({ getValue }) => shortTypeName(getValue()),
  }),
  helper.display({
    id: "exception",
    header: "Fout",
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span>{shortTypeName(row.original.exceptionType)}</span>
        <span className="max-w-md truncate text-xs opacity-70">
          {row.original.exceptionMessage}
        </span>
      </div>
    ),
  }),
  helper.accessor("sentAt", {
    header: "Verzonden",
    cell: ({ getValue }) => formatTimestamp(getValue()),
  }),
  helper.accessor("replayable", {
    id: "status",
    header: "Status",
    cell: ({ getValue }) => (
      <Badge variant={getValue() ? "orange" : "red"} size="sm">
        {getValue() ? "Wacht op replay" : "Mislukt"}
      </Badge>
    ),
  }),
  helper.display({
    id: "actions",
    meta: { action: true, headerClassName: "w-0" },
    cell: ({ row }) => <DeadLetterActions deadLetter={row.original} />,
  }),
]);

function QueueAdminOverview() {
  const { deadLetters, totalPages, totalCount } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const router = useRouter();

  return (
    <Page>
      <Page.Header>
        <Page.Title>Mislukte berichten</Page.Title>
        <Page.Header.Actions>
          <Page.Header.SecondaryActions>
            <Button variant="outline" size="sm" onClick={() => void router.invalidate()}>
              Vernieuw
            </Button>
          </Page.Header.SecondaryActions>
        </Page.Header.Actions>
      </Page.Header>
      <PendingContent>
        {deadLetters.length === 0 ? (
          <Page.Container>
            <p className="py-8 opacity-70">{m["admin.queue.geen_mislukte"]()}</p>
          </Page.Container>
        ) : (
          <DataTable
            alignToContainer
            aria-label="Mislukte berichten"
            columns={columns}
            data={deadLetters}
            getRowId={(deadLetter) => deadLetter.id}
          />
        )}

        <Page.Container>
          <Pager
            totalPages={totalPages}
            totalCount={totalCount}
            className={"mb-6 border-0"}
            page={search.page}
            onPageChange={(nextPage) =>
              navigate({
                search: (prev) => ({ ...prev, page: nextPage }),
                replace: true,
              })
            }
          />
        </Page.Container>
      </PendingContent>
    </Page>
  );
}
